import { z } from 'zod';

export const chapterSchema = z.object({
  id: z.string(),
  number: z.number().int().min(1).max(18),
  titleMarathi: z.string().min(1),
  titleEnglish: z.string().min(1),
  titleSanskrit: z.string().min(1),
  descriptionMarathi: z.string(),
  descriptionEnglish: z.string(),
  totalOvis: z.number().int().positive(),
  totalShlokas: z.number().int().positive(),
  imageUrl: z.string().url().or(z.string().length(0)),
  audioSegments: z.array(z.object({
    audioUrl: z.string(),
    oviFrom: z.number().int().positive(),
    oviTo: z.number().int().positive(),
    durationMs: z.number().int().nonnegative(),
  })),
  sortOrder: z.number().int(),
});

export const oviSchema = z.object({
  id: z.string(),
  chapterId: z.string(),
  chapterNumber: z.number().int().min(1).max(18),
  oviNumber: z.number().int().positive(),
  textMarathi: z.string().min(1),
  arthMarathi: z.string(),
  textEnglish: z.string().optional(),
  audioSegmentIndex: z.number().int().nonnegative(),
  searchText: z.string(),
  sortOrder: z.number().int(),
});

export const mauliQuestionSchema = z.object({
  id: z.string().min(1),
  q: z.string().min(1),
  options: z.array(z.string().min(1)).length(4),
  answer: z.number().int().min(0).max(3),
  explanation: z.string().min(1),
});

export const mauliStorySchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  order: z.number().int().positive(),
  title: z.string().min(1),
  era: z.string(),
  summary: z.string().min(1),
  image: z.string().optional(),
  paragraphs: z.array(z.string().min(1)).min(1),
  questions: z.array(mauliQuestionSchema).min(3).max(12),
});

export const storiesFileSchema = z.object({
  version: z.string().min(1),
  generatedAt: z.string(),
  language: z.string(),
  sourceNote: z.string(),
  stories: z.array(mauliStorySchema).min(1),
});

/**
 * Checks a stories.json payload beyond its shape: ids and order numbers must
 * be unique, question ids unique across the whole file. Returns a list of
 * problems, empty when the file is good.
 */
export function validateStoriesFile(raw: unknown): string[] {
  const parsed = storiesFileSchema.safeParse(raw);
  if (!parsed.success) {
    return parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`);
  }
  const problems: string[] = [];
  const ids = new Set<string>();
  const orders = new Set<number>();
  const qids = new Set<string>();
  for (const s of parsed.data.stories) {
    if (ids.has(s.id)) problems.push(`duplicate story id ${s.id}`);
    ids.add(s.id);
    if (orders.has(s.order)) problems.push(`duplicate story order ${s.order}`);
    orders.add(s.order);
    for (const q of s.questions) {
      if (qids.has(q.id)) problems.push(`duplicate question id ${q.id}`);
      qids.add(q.id);
      if (new Set(q.options).size !== 4) problems.push(`${q.id}: options repeat`);
    }
  }
  return problems;
}

const bilingualSchema = z.object({
  mr: z.string().trim().min(1),
  en: z.string().optional(),
});

/** What a ज्ञानकिरण card must carry before it can be scheduled or published. */
export const dnyankiranCardSchema = z.object({
  number: z.number().int().positive(),
  category: z.enum(['khagol', 'vidnyan', 'nisarg', 'tantradnyan', 'arogya', 'man', 'sanskruti', 'jivanmulye']),
  title: bilingualSchema,
  ovi: z.object({
    chapterId: z.string().regex(/^\d{2}$/),
    oviNumber: z.number().int().positive(),
    textMarathi: z.string().trim().min(1),
  }),
  oviEnglish: z.string().optional(),
  explanation: bilingualSchema,
  teaching: bilingualSchema,
  illustrationUrl: z.string().url().optional(),
  shareImageUrl: z.object({ mr: z.string().url().optional(), en: z.string().url().optional() }).optional(),
  videoUrl: z.object({ mr: z.string().url().optional(), en: z.string().url().optional() }).optional(),
});

/**
 * Problems that stop a card going out, as plain sentences. A draft may be
 * saved with any of these; scheduling and publishing refuse until the list
 * is empty.
 */
export function validateDnyankiranCard(raw: unknown): string[] {
  const parsed = dnyankiranCardSchema.safeParse(raw);
  if (!parsed.success) {
    return parsed.error.issues.map((i) => `${i.path.join('.') || 'card'}: ${i.message}`);
  }
  const problems: string[] = [];
  const c = parsed.data;
  if (!c.illustrationUrl && !c.shareImageUrl?.mr) {
    problems.push('needs an illustration or the Marathi share image — the card has nothing to show as its picture');
  }
  return problems;
}

export const phoneNumberSchema = z.string().regex(
  /^\+91\d{10}$/,
  'Phone number must be in +91XXXXXXXXXX format'
);

export const notificationSchema = z.object({
  title: z.string().min(1).max(100),
  body: z.string().min(1).max(500),
  imageUrl: z.string().url().optional(),
  targetOviId: z.string().optional(),
  type: z.enum(['announcement', 'daily_verse', 'update']),
  scheduledAt: z.date().optional(),
});
